// A lazily drilled Bryntum Gantt: the top rows from `levels.root()`, a row's children from
// `levels[row.level](row)` the first time it is expanded, bars from each row's start to its end
// date, a status picker in the header, expand/collapse all, zoom in/out/to fit, and a reload on
// every filter tick. Schema-agnostic by rule: no project SQL, no table names, no store —
// everything domain-specific arrives through the function props below (PLATFORM.md §3.2
// rules 1–3). Born at IA_ABSORPTION_PLAN Phase 4 step 13b from issue-analysis's own
// `components/gantts/IssueDueByGantt.vue` (its SQL is now the app config
// `useIssueDueByGanttChart`).
//
//   props:  title             (String)   rendered as-is; already translated
//           levels            (Object)   { root: () => Promise<Row[]>,
//                                          [level]: (parentRow) => Promise<Row[]> } — a row's
//                                          children come from the loader named by ITS `level`
//           dateRange         (Function) () => Promise<{ start, end } | null> — the span the
//                                          time axis fits (view preset + zoom to fit)
//           statusOptions     (Array)    [{ title, value, color }] for the header picker;
//                                          null (default) hides the picker
//           selectedStatuses  (Array)    v-model:selectedStatuses — the picker's selection; a
//                                          change after the first load reloads the Gantt
//           filterContext     (Object)   the filter-context duck type; a tick reloads
//           dataLoaded        (Boolean)  gates the first load
//   opt-ins (asset-management's inspection-schedule Gantt, AM_ABSORPTION_PLAN step D6; every one
//   off by default, and off the base builds, loads and emits exactly what it did before):
//           columns           (Array)    [{ type?, text?|textKey?, width?, field?, renderer? }] —
//                                          the grid's columns instead of Name / Start / Due; an
//                                          entry without `type` is the app's: its `renderer(row,
//                                          value)` gets the loader's row (read at mount)
//           childPageSize     (Object)   { [parentLevel]: n } — that level's loader gets
//                                          `(parentRow, { offset, limit: n })`; a full page ends in
//                                          a "load more" row that loads the next page in its place
//           restoreExpansion  (Boolean)  a reload re-opens the rows (by their `key`) the user had
//                                          open
//           ownFilterId       (String)   the filter id the app writes for `taskSelected`: the tick
//                                          of that write does not reload, and a reload's own
//                                          deselection is not reported (the filter stays)
//           pinWidth          (Boolean)  the card fits its column (no outer margin, min-width 0,
//                                          max-width 100%); the timeline scrolls inside it
//   emits:  update:selectedStatuses (Array)  the picker's new selection
//           taskSelected (Row | null)        the selected row (the loader's object + its `id`),
//                                            null when the selection is cleared
//
// Row: { name, level, startDate, endDate, hasChildren, color?, statusTitle?, …the app's own
// fields } — dates in any form ../../helpers/ganttDates.js's `parseDate` reads; the base assigns
// the task ids. There is deliberately NO `chartFontColor` prop: the theme — Bryntum's stylesheet
// and the one colour the base sets itself — comes from `useChartTheme()`.
//
// Full contract: ../../../README.md. The logic lives in ./setup.js.
//
// Bryntum's stylesheet is NOT imported here (it was until 2026-10-13): it follows the theme mode
// — light = Material, the one issue-analysis shipped; dark = Classic-Dark — and is put in ONE
// managed <style> by ../../helpers/bryntumTheme.js (a `?inline` import per theme, loaded on first
// mount; see the README "Theme"). An app still pays for Bryntum's CSS only when a Gantt mounts.
import setup from './setup.js';

export default {
  name: 'base-gantt-chart',
  props: {
    // Rendered as-is — already translated by the page
    title: {
      type: String,
      default: ''
    },
    // { root: () => Promise<Row[]>, [level]: (parentRow) => Promise<Row[]> }
    levels: {
      type: Object,
      required: true
    },
    // () => Promise<{ start, end } | null>: the time axis' span (the zoom-to-fit range)
    dateRange: {
      type: Function,
      default: null
    },
    // [{ title, value, color }]; null hides the status picker
    statusOptions: {
      type: Array,
      default: null
    },
    // v-model:selectedStatuses — the picker's selection (the loaders read the app's copy)
    selectedStatuses: {
      type: Array,
      default: () => []
    },
    // The filter-context duck type: a tick reloads the Gantt from the top
    filterContext: {
      type: Object,
      required: true
    },
    dataLoaded: {
      type: Boolean,
      default: false
    },
    // ---- opt-ins (AM_ABSORPTION_PLAN D6) — all off by default: the README's "Opt-ins"
    // [{ type?: 'name'|'startdate'|'enddate', text?, textKey?, width?, field?, renderer? }]; null =
    // the three built-in columns (Name 300, Start Date 130, Due Date 130). Read at mount.
    columns: {
      type: Array,
      default: null
    },
    // { [parentLevel]: pageSize } — the children of a row of that level load a page at a time
    childPageSize: {
      type: Object,
      default: null
    },
    // A reload re-opens the rows the user had open (rows identified by their `key` field)
    restoreExpansion: {
      type: Boolean,
      default: false
    },
    // The id of the filter the app registers for `taskSelected` ('' = none)
    ownFilterId: {
      type: String,
      default: ''
    },
    // The card fits its column: no outer margin, min-width 0, max-width 100%, overflow hidden
    pinWidth: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:selectedStatuses', 'taskSelected'],
  setup,
};
